/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */

import React, { FC, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, PieLabelRenderProps } from 'recharts';

import { ForecastListItem } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { useIntl } from '@futrli/localisation';

import {
  AxisOptions,
  DataField,
  FinData,
  FinFieldOptions,
  PieDonutData,
  ROUNDING_OPTIONS,
  VALUE_DISPLAY_OPTIONS
} from '../../types';
import { getTextWidth } from '../../utils-generic';
import { chartLabelFontFamily, chartLabelFontSize, chartLabelFontWeight, noDataColors } from '../../constants';
import { generateDefaultColors, getTextColorBasedOnBackground } from '../../utils-colors';
import { getDataForPieDonutChart } from '../../utils-data';
import { PieDonutCustomLegend } from './pie-donut-custom-legend';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './report-charts.scss';

interface Props {
  data: (FinData | undefined)[];
  fields: DataField[];
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[];
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[];
  primaryAxisOptions: AxisOptions | undefined;
  secondaryAxisOptions: AxisOptions | undefined;
  currency: string;
  isDonut: boolean;
  ariaLabel?: string;
}
export const ReportPieDonutChart: FC<Props> = ({
  data,
  fields,
  selectedForecasts,
  primaryAxisOptions,
  finFieldsOptions,
  currency,
  isDonut,
  ariaLabel
}: Props) => {
  const hasData = !!(data[0] && data[0].periods.length);
  const intl = useIntl();
  const { roundAndFormatValue } = useI18nConstants();
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const primaryFields = useMemo(() => fields.filter(f => !f.isSecondary), [fields]);

  const colors = useMemo(() => {
    const chartColors = generateDefaultColors(primaryFields.length);

    const getColor = (c: string[] | Record<string, string> | undefined, ci: number) => {
      if (c && Object.keys(c).length) {
        const keys: string[] = Object.keys(c);
        return (c as Record<string, string>)[keys[0]] || chartColors[ci];
      } else if (Array.isArray(c)) {
        return c[0] || chartColors[ci];
      } else {
        return chartColors[ci];
      }
    };
    return primaryFields.map((f, i) => getColor(finFieldsOptions.find(e => e.ref === f.ref)?.options?.colors, i));
  }, [primaryFields, finFieldsOptions]);

  const pieDonutData = useMemo(() => {
    return getDataForPieDonutChart(
      selectedForecasts,
      primaryFields,
      primaryAxisOptions,
      colors,
      noDataColors,
      data,
      finFieldsOptions
    );
  }, [data, primaryFields, primaryAxisOptions, selectedForecasts, colors, finFieldsOptions]);

  const labelFormatter = useCallback(
    (value: number | string) => {
      let unit = primaryAxisOptions?.unit;
      if (!unit) {
        const isCurrency = primaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.CURRENCY
        );
        const isPercentage = primaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.PERCENTAGE
        );
        unit = isCurrency
          ? VALUE_DISPLAY_OPTIONS.CURRENCY
          : isPercentage
          ? VALUE_DISPLAY_OPTIONS.PERCENTAGE
          : VALUE_DISPLAY_OPTIONS.NUMBER;
      }
      return roundAndFormatValue(value, primaryAxisOptions?.rounding || ROUNDING_OPTIONS.INTEGER, unit, intl, currency);
    },
    [primaryAxisOptions?.unit, primaryAxisOptions?.rounding, roundAndFormatValue, intl, currency, primaryFields, data]
  );

  const formatTooltip = useCallback(
    (value: number, _name: string, props: any): string[] => {
      const [ds, ref] = props.payload.uuid.split('#|#');
      const label =
        selectedForecasts.length > 1
          ? `${primaryFields.find(f => f.ref === ref)?.name || ref} - ${
              selectedForecasts.find(sf => sf.id === ds)?.name || ds
            }`
          : primaryFields.find(f => f.ref === ref)?.name || ref;

      return [labelFormatter(value), label];
    },
    [labelFormatter, primaryFields, selectedForecasts]
  );

  // data will take 2 spaces and padding between circles will be 1 space.
  const spacePerCircle = useMemo(() => 70 / (3 * (pieDonutData.length - 1) + 2), [pieDonutData.length]);

  const handleResize = (w: number, h: number) => {
    setChartHeight(h);
    setChartWidth(w);
  };

  const renderCustomizedLabel = useCallback(
    (
      pie: PieDonutData, // Data for the pie/donut chart, includes the colors of each segment
      { cx, cy, midAngle, innerRadius, outerRadius, value, index, percent }: PieLabelRenderProps // Position and size properties for each pie slice
    ) => {
      // Parse percentage and radiuses for calculations, ensure they are numbers
      const p = +(percent || 0);
      const ir = +(innerRadius || 0); // Inner radius for donut charts, 0 for pie charts
      const or = +(outerRadius || 0); // Outer radius for the slice

      // Avoid rendering labels for very small slices to prevent overlap and clutter
      // the percentage for outer circles can be smaller because it has more space
      if ((p < 0.02 && innerRadius === 0) || (p < 0.01 && ir > 0)) {
        return null;
      }

      // Parse the center coordinates of the chart
      const nx = +(cx || 0);
      const ny = +(cy || 0);

      // Convert degrees to radians for angle calculations
      const RADIAN = Math.PI / 180;

      // Calculate the middle radius to position the label inside the slice
      const radius = ir + (or - ir) * 0.5;

      // Format the value for display
      const formattedValue = labelFormatter(value as number);

      // Calculate the text width to position the label correctly
      const labelWidth = getTextWidth(
        formattedValue,
        `${chartLabelFontWeight} ${chartLabelFontSize}px ${chartLabelFontFamily}`
      );

      // Calculate the x and y positions for the label, based on the angle and radius
      const x = nx + radius * Math.cos(-(midAngle as number) * RADIAN);
      const y = ny + radius * Math.sin(-(midAngle as number) * RADIAN);

      // Return the text element, positioned dynamically within the slice
      return (
        <text
          // Adjust the X position depending on which side of the chart the label is on
          x={x > nx ? x - labelWidth / 2 : x + labelWidth / 2}
          y={y}
          // Set text color based on the background color of the pie slice
          fill={pie.data[index || 0]?.color ? getTextColorBasedOnBackground(pie.data[index || 0]?.color) : undefined}
          // Align the text properly depending on its X position relative to the center
          textAnchor={x > nx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize={`${chartLabelFontSize}px`}
          fontWeight={chartLabelFontWeight}
          fontFamily={chartLabelFontFamily}
        >
          {formattedValue}
        </text>
      );
    },
    [labelFormatter]
  );

  return (
    <div className={clsx(styles.chartAndLegendWrapper)}>
      <div className={clsx(styles.chartWrapper)}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 1, height: 1 }}
          onResize={handleResize}
          style={{ overflow: 'visible' }}
          aria-label={ariaLabel}
        >
          <PieChart style={{ overflow: 'visible' }} margin={{ top: 15, right: 0, bottom: 0, left: 0 }}>
            {hasData && (
              <Tooltip
                formatter={formatTooltip}
                wrapperStyle={{ fontFamily: 'SageUI', fontSize: 8, lineHeight: 1, fontWeight: 500 }}
              />
            )}
            {pieDonutData.map((pie, i) => {
              const cut = !isDonut && i === 0 ? 0 : pieDonutData.length > 1 ? 30 : 60;
              const inner = pieDonutData.length > 1 ? cut + i * 3 * spacePerCircle : cut;
              const outer = pieDonutData.length > 1 ? 30 + (i * 3 + 2) * spacePerCircle : 100;
              return (
                <Pie
                  startAngle={90}
                  endAngle={-270}
                  key={pie.forecastName}
                  data={pie.data}
                  dataKey="value"
                  innerRadius={`${inner}%`}
                  outerRadius={`${outer}%`}
                  labelLine={false}
                  label={props => (hasData ? renderCustomizedLabel(pie, props) : null)}
                  isAnimationActive={false}
                  aria-hidden="true"
                >
                  {pie.data.map((_, i) => (
                    <Cell key={`cell-${i}`} fill={pie.data[i].color} />
                  ))}
                </Pie>
              );
            })}
            {hasData &&
              pieDonutData.length > 1 &&
              pieDonutData.map((pie, i) => {
                const outer = pieDonutData.length > 1 ? 30 + (i * 3 + 2) * spacePerCircle : 100;
                const adjustment = chartHeight - 15 > chartWidth ? (chartHeight - 15 - chartWidth) / 2 : 0;
                const chartDiameter = chartHeight - 15 > chartWidth ? chartWidth : chartHeight - 15;
                return (
                  <text
                    tabIndex={0}
                    key={pie.forecastName}
                    x={chartWidth / 2}
                    y={10 + adjustment + chartDiameter * 0.5 * (1 - outer / 100)}
                    fill="black"
                    fontSize={9}
                    fontWeight={500}
                    fontFamily='"SageUI", sans-serif'
                    textAnchor="middle"
                  >
                    {pie.forecastName}
                  </text>
                );
              })}
          </PieChart>
        </ResponsiveContainer>
      </div>
      {hasData && <PieDonutCustomLegend pieDonutData={pieDonutData} />}
    </div>
  );
};
